import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, ChevronDown, RefreshCw, Trash2, Pencil, Truck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { StatusBadge } from '@/components/ui/status-badge'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { useTask } from '@/hooks/useTask'
import { useTaskKMCodes } from '@/hooks/useTaskEntities'
import { useTaskCountersWebSocket } from '@/hooks/useTaskCountersWebSocket'
import { useToast } from '@/hooks/useToast'
import { tasksApi } from '@/lib/api'
import { extractApiError } from '@/lib/api'
import { TASK_STATUS } from '@/lib/constants'
import { formatDate, formatDateTime } from '@/lib/utils'
import { useEffect, useRef, useState } from 'react'
import { TaskEntitiesPanel } from '@/components/features/tasks/TaskEntitiesPanel'
import { TaskDuplicateLogPanel } from '@/components/features/tasks/TaskDuplicateLogPanel'
import { PrintAgentBadge } from '@/components/features/tasks/PrintAgentBadge'
import { AssignDatesModal, type AssignDatesTarget } from '@/components/features/tasks/AssignDatesModal'

const CREATION_MODE_LABELS: Record<string, string> = {
  km_and_ka_file: 'КМ + КА из файлов',
  ka_file_only: 'Только КА из файлов',
}

export function TaskDetailPage() {
  const { taskId: taskUuid } = useParams<{ taskId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { data: task, isLoading, refetch, isFetching } = useTask(taskUuid)
  const countersLive = useTaskCountersWebSocket(taskUuid)
  const kmQuery = useTaskKMCodes({
    taskUuid,
    tab: 'codes',
    activeTab: 'codes',
    page: 1,
    search: '',
  })
  const [isDeleting, setIsDeleting] = useState(false)
  const [isCreatingShipment, setIsCreatingShipment] = useState(false)
  const [createMenuOpen, setCreateMenuOpen] = useState(false)
  const createMenuRef = useRef<HTMLDivElement>(null)
  const [datesTarget, setDatesTarget] = useState<AssignDatesTarget | null>(null)

  useEffect(() => {
    if (!createMenuOpen) return
    const onDocClick = (e: MouseEvent) => {
      if (createMenuRef.current && !createMenuRef.current.contains(e.target as Node)) {
        setCreateMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [createMenuOpen])

  const handleCreateShipment = async () => {
    if (!taskUuid) return
    setCreateMenuOpen(false)
    setIsCreatingShipment(true)
    try {
      const { data } = await tasksApi.createShipment(taskUuid)
      const shipmentId = data.shipment?.id
      toast.success(
        shipmentId
          ? `Отгрузка ${shipmentId} создана (${data.additions_count ?? 0} поз.)`
          : 'Отгрузка создана',
      )
      if (shipmentId) {
        navigate(`/admin/shipments/${shipmentId}`)
      }
    } catch (err) {
      toast.error(extractApiError(err))
    } finally {
      setIsCreatingShipment(false)
    }
  }

  const handleDelete = async () => {
    if (!taskUuid) return
    if (!confirm(`Удалить задание ${task?.id || taskUuid}?`)) return

    setIsDeleting(true)
    try {
      await tasksApi.delete(taskUuid)
      toast.success('Задание удалено')
      navigate('/admin/tasks')
    } catch (err) {
      toast.error(extractApiError(err))
    } finally {
      setIsDeleting(false)
    }
  }

  if (isLoading) return <LoadingSpinner className="mt-20" />

  if (!task) {
    return (
      <div className="text-center py-16 text-gray-400">
        <p className="text-lg">Задание не найдено</p>
        <Link to="/admin/tasks">
          <Button variant="ghost" className="mt-4">
            Назад к списку
          </Button>
        </Link>
      </div>
    )
  }

  const statusMeta = TASK_STATUS[task.status]
  const codesCount = Math.max(task.codes_count, kmQuery.data?.total ?? 0)

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/admin/tasks">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{task.id}</h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <StatusBadge color={statusMeta.color} textColor={statusMeta.textColor}>
                {statusMeta.label}
              </StatusBadge>
              <PrintAgentBadge />
              {task.creation_mode && (
                <span className="text-xs text-gray-400">
                  {CREATION_MODE_LABELS[task.creation_mode] ?? task.creation_mode}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => refetch()} isLoading={isFetching}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <div className="relative" ref={createMenuRef}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setCreateMenuOpen(open => !open)}
              isLoading={isCreatingShipment}
              aria-expanded={createMenuOpen}
              aria-haspopup="menu"
            >
              Создать на основании
              <ChevronDown className={`w-4 h-4 transition-transform ${createMenuOpen ? 'rotate-180' : ''}`} />
            </Button>
            {createMenuOpen && (
              <div
                role="menu"
                className="absolute z-50 top-full right-0 mt-1 min-w-[200px] bg-white border border-gray-200 rounded-lg shadow-lg py-1"
              >
                <button
                  type="button"
                  role="menuitem"
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-2"
                  onClick={handleCreateShipment}
                >
                  <Truck className="w-4 h-4 text-blue-600 shrink-0" />
                  <span>Отгрузка</span>
                </button>
              </div>
            )}
          </div>
          <Button variant="danger" size="sm" onClick={handleDelete} isLoading={isDeleting}>
            <Trash2 className="w-4 h-4 mr-1" /> Удалить
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Информация</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Название:</span>
            <p className="font-medium">{task.name || '—'}</p>
          </div>
          <div>
            <span className="text-gray-500">Дата производства:</span>
            <div className="flex items-center gap-2">
              <p className="font-medium">{formatDate(task.production_date)}</p>
              <button
                type="button"
                title="Присвоить дату производства"
                className="p-1 rounded hover:bg-gray-100 text-gray-500"
                onClick={() =>
                  setDatesTarget({
                    scope: 'task',
                    productionDate: task.production_date,
                    expiryDate: task.expiry_date,
                  })
                }
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <div>
            <span className="text-gray-500">Создано:</span>
            <p className="font-medium">{formatDateTime(task.created_at)}</p>
          </div>
          <div>
            <span className="text-gray-500">Срок годности:</span>
            <div className="flex items-center gap-2">
              <p className="font-medium">{formatDate(task.expiry_date)}</p>
              <button
                type="button"
                title="Присвоить срок годности"
                className="p-1 rounded hover:bg-gray-100 text-gray-500"
                onClick={() =>
                  setDatesTarget({
                    scope: 'task',
                    productionDate: task.production_date,
                    expiryDate: task.expiry_date,
                  })
                }
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          Счётчики
          <span
            className={`text-xs font-normal ${countersLive ? 'text-green-600' : 'text-gray-400'}`}
            title={countersLive ? 'Счётчик КМ обновляется по WebSocket' : 'WebSocket отключён, переподключение…'}
          >
            {countersLive ? '● live' : '○ offline'}
          </span>
        </h2>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-gray-500 text-xs">Коды (КМ)</p>
            <p className="text-2xl font-bold text-green-600">{codesCount}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs">Коробки</p>
            <p className="text-2xl font-bold text-cyan-600">{task.boxes_count}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs">Паллеты</p>
            <p className="text-2xl font-bold text-blue-600">{task.pallets_count}</p>
          </div>
        </div>
      </div>

      <TaskEntitiesPanel task={task} />

      {task.creation_mode === 'ka_file_only' && <TaskDuplicateLogPanel taskUuid={task.uuid} />}

      <AssignDatesModal
        taskUuid={task.uuid}
        target={datesTarget}
        onClose={() => setDatesTarget(null)}
      />
    </div>
  )
}
